import React from 'react';
import { ItemToSchedule } from '../../contexts/ModuleContext';
import { PatientProps } from '../../contexts/PatientContext';
import { ProductRuleResponse, TYPE_ROUTE_PRODUCT } from '../../hooks/useProductRuleActions';
import { useSchedulingMountMulti } from '../../hooks/useSchedulingMountMulti';
import { CoverageProps, ProductItemProps, Rule } from '../../models/Product';
import { Payment, ScheduleItemProps } from '../../models/Schedule';
import { AdditionalInformations } from '../../modules/validateRules';
import { PAGES } from '../../Router/pages';
import { INFO_GENERIC_RULES_CONDITION, INFO_GENERIC_RULES_WHEN } from '../../utils/constants';
import { verifyGenericModalCondition } from '../../utils/genericModals';
import { HandleSelectProductProps } from './components/ProductList';
import { ProductCheck } from './components/ProductListCheck';
import { useAnalytics } from '../../hooks/useAnalytics';
import { useServiceContext } from '../../contexts/ServiceContext';
import { FirebaseEvents } from '../../utils/enums/firebase-events';

export type useProductSelectorProps = {
  logged: boolean;
  selectedItemToSchedule: ItemToSchedule;
  scheduleSelected: ScheduleItemProps | undefined;
  goTo: (route: number, state?: any) => void;
  patientSelected: PatientProps | undefined;
  openModal: (when: string, condition: string) => void;
  addProductsToCart: (product: ProductItemProps[]) => void;
  selectItemToSchedule: (value: ItemToSchedule) => void;
  selectProductScheduleModule: (product: ProductItemProps) => void;
  changeSchedule: (schedule: ScheduleItemProps) => ScheduleItemProps;
  handleGenericModals(rules: Rule[]): AdditionalInformations[] | undefined;
  createSchedule: (props: {
    product: ProductItemProps;
    patient: PatientProps;
    itemToSchedule: ItemToSchedule;
    payment?: Payment;
  }) => Promise<ProductRuleResponse | undefined>;
  handleSelectedCoverage: (props: {
    coverage: CoverageProps;
    patientSelected: PatientProps | undefined;
  }) => Promise<void>;
};

export const useProductSelector = ({
  selectProductScheduleModule,
  selectedItemToSchedule,
  handleSelectedCoverage,
  selectItemToSchedule,
  handleGenericModals,
  addProductsToCart,
  patientSelected,
  createSchedule,
  openModal,
  logged,
  goTo
}: useProductSelectorProps) => {
  const [productList, setProductList] = React.useState<ProductItemProps[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [selectedProduct, setSelectedProduct] = React.useState<ProductItemProps>();
  const [productListCheck, setProductListCheck] = React.useState<ProductCheck[]>([]);

  const useScheduling = useSchedulingMountMulti();
  const { selectedService } = useServiceContext();
  const { logEvent } = useAnalytics();

  React.useEffect(() => {
    setProductListCheck(productList.map(item => ({ ...item, selected: false, disable: false })));
  }, [productList]);

  function handleSelectProduct({ product }: HandleSelectProductProps) {
    setSelectedProduct(product);
    selectProductScheduleModule(product);

    logEvent({
      eventName: FirebaseEvents.schedule_selected_product,
      eventParams: {
        product_id: product.id,
        product_name: product.name,
        service_id: selectedService.id,
        service_name: selectedService.name
      }
    });
  }

  function checkItemToSchedule() {
    if (selectedItemToSchedule === null) selectItemToSchedule('PRODUCT');
    if (selectedItemToSchedule === 'SERVICE') selectItemToSchedule('PRODUCT');
  }

  function checkMandatoryModal(product: ProductItemProps, when: string) {
    const genericModals = product.rules ? handleGenericModals(product.rules) : undefined;
    if (genericModals) {
      const modal = verifyGenericModalCondition({
        genericModals: genericModals,
        condition: INFO_GENERIC_RULES_CONDITION.always,
        when
      });
      return modal;
    }
  }

  function handleProductWithoutPatient(product: ProductItemProps) {
    handleSelectProduct({ product });
    goTo(PAGES.PATIENT_FORM.id, { isAps: true });
  }

  function handleB2CProduct(product: ProductItemProps) {
    handleSelectProduct({ product });
    goTo(PAGES.RESOURCE_DETAILS.id, { previousPage: true });
  }

  async function routeProductSelector({
    productProp,
    alreadyShowModal
  }: {
    productProp?: ProductItemProps;
    alreadyShowModal?: boolean;
  }) {
    setLoading(true);
    checkItemToSchedule();
    if (selectedProduct?.is_b2b) {
      const productSchedule = productProp || selectedProduct;

      if (productSchedule && patientSelected) {
        const modal = checkMandatoryModal(selectedProduct, INFO_GENERIC_RULES_WHEN.after_patient_identification);

        if (modal && !alreadyShowModal)
          openModal(INFO_GENERIC_RULES_WHEN.after_patient_identification, INFO_GENERIC_RULES_CONDITION.always);
        else {
          const scheduleRoute = await createSchedule({
            product: productSchedule,
            patient: patientSelected,
            itemToSchedule: 'PRODUCT'
          });

          if (scheduleRoute?.type === INFO_GENERIC_RULES_CONDITION.is_first_appointment)
            openModal(INFO_GENERIC_RULES_WHEN.after_patient_identification, scheduleRoute.type);

          if (scheduleRoute?.type === TYPE_ROUTE_PRODUCT.only_telemedicine && scheduleRoute.coverage)
            handleSelectedCoverage({
              coverage: scheduleRoute.coverage,
              patientSelected
            });
        }
      }
      if (!patientSelected && productSchedule) handleProductWithoutPatient(productSchedule);
    }
    if (selectedProduct && !selectedProduct?.is_b2b) handleB2CProduct(selectedProduct);

    setLoading(false);
  }

  function handleMultiselectedProducts() {
    const selectedProducts = productListCheck.filter(item => item.selected);
    addProductsToCart(selectedProducts);

    if (!logged) {
      const schedulesToSave = useScheduling.createMultipleSchedulesByProduct(selectedProducts);

      useScheduling.saveSchedules(schedulesToSave);

      goTo(PAGES.SERVICE_TYPE_PAGE.id);
    }
  }

  return {
    loading,
    setLoading,
    productList,
    setProductList,
    selectedProduct,
    productListCheck,
    setProductListCheck,
    handleSelectProduct,
    checkMandatoryModal,
    routeProductSelector,
    handleMultiselectedProducts
  };
};
